import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  normalizeSupportedLanguage,
  type SupportedLanguage,
  type LocalizedStringMap,
  type AnswerKey,
} from "@petergok/blue-collar-shared";

import { StorageKeys } from "@/utils/storage";
import en from "./locales/en.json";
import es from "./locales/es.json";

const BOOT_LOADING_LABELS: Record<SupportedLanguage, string> = {
  en: en.common.loading,
  es: es.common.loading,
};

export {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  normalizeSupportedLanguage,
};
export type { SupportedLanguage, LocalizedStringMap, AnswerKey };

export function getSelectableLanguages(): SupportedLanguage[] {
  return [...SUPPORTED_LANGUAGES];
}

export function isLanguageSelectable(
  language: SupportedLanguage,
): boolean {
  return getSelectableLanguages().includes(language);
}

export function getStoredLanguage(): SupportedLanguage | null {
  // localStorage can throw on Safari ITP / private browsing modes; falling
  // through with null lets us boot on the navigator language instead of
  // leaving i18n uninitialized (which would render raw key strings).
  try {
    const stored = localStorage.getItem(StorageKeys.SELECTED_LANGUAGE);
    const normalized = normalizeSupportedLanguage(stored);
    return isLanguageSelectable(normalized) ? normalized : null;
  } catch {
    return null;
  }
}

function getNavigatorLanguage(): SupportedLanguage {
  const candidate =
    (typeof navigator !== "undefined" &&
      (navigator.language || (navigator.languages && navigator.languages[0]))) ||
    DEFAULT_LANGUAGE;
  const normalized = normalizeSupportedLanguage(candidate);
  return isLanguageSelectable(normalized) ? normalized : DEFAULT_LANGUAGE;
}

function getQueryLanguage(): SupportedLanguage | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("lang");
    if (!value) return null;
    const normalized = normalizeSupportedLanguage(value);
    return isLanguageSelectable(normalized) ? normalized : null;
  } catch {
    return null;
  }
}

function resolveInitialLanguage(): SupportedLanguage {
  return (
    getStoredLanguage() ?? getQueryLanguage() ?? getNavigatorLanguage()
  );
}

let initializationPromise: Promise<SupportedLanguage> | null = null;
let storageListenerInstalled = false;

function installStorageListener() {
  if (storageListenerInstalled || typeof window === "undefined") return;
  window.addEventListener("storage", (event) => {
    if (event.key !== StorageKeys.SELECTED_LANGUAGE) return;
    const next = normalizeSupportedLanguage(event.newValue);
    if (
      isLanguageSelectable(next) &&
      i18n.language !== next
    ) {
      // changeLanguage fires the languageChanged event which the bootstrap
      // handler in main.tsx uses to update <html lang>, document.title, and
      // og:locale. No need to set documentElement.lang directly here.
      void i18n.changeLanguage(next);
    }
  });
  storageListenerInstalled = true;
}

export async function ensureI18nInitialized(): Promise<SupportedLanguage> {
  if (!initializationPromise) {
    initializationPromise = (async () => {
      const initialLanguage = resolveInitialLanguage();

      if (!i18n.isInitialized) {
        await i18n.use(initReactI18next).init({
          lng: initialLanguage,
          fallbackLng: DEFAULT_LANGUAGE,
          interpolation: { escapeValue: false },
          resources: {
            en: { translation: en },
            es: { translation: es },
          },
          // Resources are bundled, so we don't need React Suspense to gate on
          // async namespace loads. Avoids requiring a Suspense boundary at
          // every useTranslation call site.
          react: { useSuspense: false },
        });
      } else if (i18n.language !== initialLanguage) {
        await i18n.changeLanguage(initialLanguage);
      }

      installStorageListener();
      return initialLanguage;
    })();
  }

  return initializationPromise;
}

export function getCurrentLanguage(): SupportedLanguage {
  return normalizeSupportedLanguage(i18n.language || DEFAULT_LANGUAGE);
}

export function getBootLoadingLabelForLanguage(
  language: SupportedLanguage,
): string {
  return (
    BOOT_LOADING_LABELS[language] || BOOT_LOADING_LABELS[DEFAULT_LANGUAGE]
  );
}

// Resolve once at module load so the splash screen / boot UI doesn't have
// to wait for ensureI18nInitialized() to render its loading label.
const cachedBootLoadingLabel = getBootLoadingLabelForLanguage(
  getStoredLanguage() ?? getNavigatorLanguage(),
);

export function getBootLoadingLabel(): string {
  return cachedBootLoadingLabel;
}

export async function changeLanguage(
  nextLanguage: SupportedLanguage,
): Promise<SupportedLanguage> {
  const normalized = normalizeSupportedLanguage(nextLanguage);
  const language = isLanguageSelectable(normalized)
    ? normalized
    : DEFAULT_LANGUAGE;

  await ensureI18nInitialized();
  // Persist best-effort: a write failure shouldn't block the in-memory
  // language change for the current session.
  try {
    localStorage.setItem(StorageKeys.SELECTED_LANGUAGE, language);
  } catch {
    // Swallow — i18n.changeLanguage below still applies for this session.
  }
  await i18n.changeLanguage(language);

  return language;
}

export default i18n;
